import React, { ReactNode, useEffect, useLayoutEffect, useState } from "react";
import { doc, getFirestore, onSnapshot } from "@firebase/firestore";

import FallbackPage from "../FallbackPage/FallbackPage";
import { LoadingCircle } from "@allaria-tech/matter/LoadingCircle";
import styles from "./MaintenanceScreen.module.scss";

interface AppStatus {
  maintenance: boolean;
  technicalProblems: boolean;
}

interface MaintenanceScreenProps {
  children: ReactNode;
}
const MaintenanceScreen: React.FC<MaintenanceScreenProps> = ({ children }) => {
  const db = getFirestore();
  const [appStatus, setAppStatus] = useState<AppStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const maintenanceScreenRef = doc(db, "configurations", "maintenanceScreen");

    const unsubscribe = onSnapshot(
      maintenanceScreenRef,
      (doc) => {
        if (doc.exists()) {
          const data = doc.data() as AppStatus;
          setAppStatus(data);
        } else {
          setAppStatus({
            maintenance: false,
            technicalProblems: false,
          });
        }
        setLoading(false);
      },
      (error) => {
        console.error("Firestore onSnapshot error:", error);
        setAppStatus({
          maintenance: false,
          technicalProblems: false,
        });
        setLoading(false);
      },
    );

    return () => {
      return unsubscribe();
    };
  }, []);

  const shouldShowFallback =
    appStatus?.maintenance || appStatus?.technicalProblems;

  const isTransparent =
    new URLSearchParams(window.location.search).get("transparent") === "true";

  useLayoutEffect(() => {
    if (!isTransparent) return;
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => {
      document.documentElement.style.background = "";
      document.body.style.background = "";
    };
  }, [isTransparent]);

  if (loading) {
    return (
      <div
        className={`${styles.loaderScreen} ${isTransparent ? styles.transparentContainer : ""}`}
      >
        <LoadingCircle />
      </div>
    );
  }

  return shouldShowFallback ? (
    <FallbackPage appStatus={appStatus} />
  ) : (
    <>{children}</>
  );
};

export default MaintenanceScreen;
